// components/SmartPhotoCapture.jsx
import React, { useState, useRef } from 'react';
import { Upload, Button, message, Modal, Spin, Tag, Alert } from 'antd';
import { CameraOutlined, DeleteOutlined, EyeOutlined, CheckCircleOutlined } from '@ant-design/icons';
import Tesseract from 'tesseract.js';

const SmartPhotoCapture = ({ 
    value = [], 
    onChange, 
    maxPhotos = 5,
    assetCatId,
    onPlateDetected,
    onConditionDetected 
}) => {
    const [photos, setPhotos] = useState(value);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisResults, setAnalysisResults] = useState({});
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const fileInputRef = useRef(null);

    // OCR Detection for Registration Plates
    const detectRegistrationPlate = async (imageUrl) => {
        try {
            setAnalyzing(true);
            message.loading({ content: 'Analyzing image for registration plate...', key: 'ocr' });
            
            const { data: { text } } = await Tesseract.recognize(imageUrl, 'eng', {
                logger: m => console.log(m)
            });
            
            // Pattern matching for various registration plate formats
            const patterns = [
                /([A-Z]{3}\s?[A-Z0-9]{3}\s?[A-Z0-9]{1,5})/g,  // XXX XXX XXXXX
                /([A-Z]{2,3}\s?\d{2,4}\s?[A-Z]{1,3})/g,        // XX 1234 X
                /(\d{1,3}\s?[A-Z]{2,3}\s?\d{1,4})/g            // 123 ABC 456
            ];
            
            let detectedPlate = null;
            for (const pattern of patterns) {
                const match = text.match(pattern);
                if (match) {
                    detectedPlate = match[0].replace(/\s+/g, ' ').trim().toUpperCase();
                    break;
                }
            }
            
            message.destroy('ocr');
            
            if (detectedPlate) {
                message.success(`Registration plate detected: ${detectedPlate}`);
                if (onPlateDetected) {
                    Modal.confirm({
                        title: 'Registration Plate Detected',
                        content: `Found: ${detectedPlate}. Apply this to the form?`,
                        onOk: () => onPlateDetected(detectedPlate),
                        okText: 'Apply',
                        cancelText: 'Ignore'
                    });
                }
                return detectedPlate;
            } else {
                message.info('No registration plate detected in this image');
                return null;
            }
        } catch (error) {
            console.error('OCR Error:', error);
            message.error('Failed to analyze image');
            return null;
        } finally {
            setAnalyzing(false);
        }
    };

    // AI Condition Assessment (simplified - you can enhance with ML)
    const assessCondition = (imageFile) => {
        return new Promise((resolve) => {
            const img = new Image();
            const reader = new FileReader();
            
            reader.onload = (e) => {
                img.src = e.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    
                    // Analyze image properties
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const brightness = calculateBrightness(imageData);
                    const sharpness = calculateSharpness(imageData);
                    
                    // Simple heuristic-based condition assessment
                    let condition = 'Unknown';
                    let confidence = 0;
                    
                    if (brightness > 150 && sharpness > 0.5) {
                        condition = 'Good condition';
                        confidence = 85;
                    } else if (brightness > 100 && sharpness > 0.3) {
                        condition = 'Fair';
                        confidence = 70;
                    } else if (brightness < 80 || sharpness < 0.2) {
                        condition = 'Waiting for repairs';
                        confidence = 65;
                    }
                    
                    resolve({ condition, confidence, brightness, sharpness });
                };
            };
            
            reader.readAsDataURL(imageFile);
        });
    };

    const calculateBrightness = (imageData) => {
        let totalBrightness = 0;
        const data = imageData.data;
        
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            totalBrightness += (r + g + b) / 3;
        }
        
        return totalBrightness / (data.length / 4);
    };

    const calculateSharpness = (imageData) => {
        // Simplified edge detection for sharpness
        const data = imageData.data;
        let edgeStrength = 0;
        
        for (let i = 0; i < data.length - 4; i += 4) {
            const diff = Math.abs(data[i] - data[i + 4]);
            edgeStrength += diff;
        }
        
        return Math.min(edgeStrength / (data.length / 4) / 255, 1);
    };

    const handlePhotoUpload = async (file) => {
        if (photos.length >= maxPhotos) {
            message.error(`Maximum ${maxPhotos} photos allowed`);
            return false;
        }

        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return false;
        }

        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Image must be smaller than 5MB!');
            return false;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const newPhoto = {
                uid: Date.now() + Math.random(),
                name: file.name,
                status: 'done',
                url: e.target.result,
                file: file,
                isNew: true
            };

            const updatedPhotos = [...photos, newPhoto];
            setPhotos(updatedPhotos);
            onChange(updatedPhotos);

            // Auto-analyze if vehicle asset
            if (assetCatId === '8') {
                const plate = await detectRegistrationPlate(e.target.result);
                if (plate) {
                    setAnalysisResults(prev => ({
                        ...prev,
                        [newPhoto.uid]: { plate }
                    }));
                }
            }

            // Assess condition
            const condition = await assessCondition(file);
            setAnalysisResults(prev => ({
                ...prev,
                [newPhoto.uid]: {
                    ...prev[newPhoto.uid],
                    condition
                }
            }));

            if (onConditionDetected && condition.confidence > 70) {
                Modal.confirm({
                    title: 'AI Condition Assessment',
                    content: (
                        <div>
                            <p>Based on photo analysis:</p>
                            <p><strong>Suggested Status:</strong> {condition.condition}</p>
                            <p><strong>Confidence:</strong> {condition.confidence}%</p>
                            <p>Apply this status to the asset?</p>
                        </div>
                    ),
                    onOk: () => onConditionDetected(condition),
                    okText: 'Apply',
                    cancelText: 'Ignore'
                });
            }
        };

        reader.readAsDataURL(file);
        return false;
    };

    const handleRemove = (photo) => {
        const updatedPhotos = photos.filter(p => p.uid !== photo.uid);
        setPhotos(updatedPhotos);
        onChange(updatedPhotos);
        
        // Remove analysis results
        const newResults = { ...analysisResults };
        delete newResults[photo.uid];
        setAnalysisResults(newResults);
    };

    const handlePreview = (photo) => {
        setPreviewImage(photo.url);
        setPreviewVisible(true);
    };

    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
                <strong>Asset Photos ({photos.length}/{maxPhotos})</strong>
                {assetCatId === '8' && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                        AI-Powered Plate Detection
                    </Tag>
                )}
            </div>

            <Spin spinning={analyzing} tip="Analyzing image...">
                <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 8,
                    marginBottom: 12 
                }}>
                    {photos.map(photo => (
                        <div key={photo.uid} style={{ 
                            position: 'relative',
                            width: 100,
                            height: 100,
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            overflow: 'hidden'
                        }}>
                            <img 
                                src={photo.url} 
                                alt={photo.name}
                                style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover' 
                                }}
                            />
                            
                            {/* Analysis Results Overlay */}
                            {analysisResults[photo.uid]?.plate && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    color: 'white',
                                    padding: '2px 4px',
                                    fontSize: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                    {analysisResults[photo.uid].plate}
                                </div>
                            )}

                            {analysisResults[photo.uid]?.condition && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    color: 'white',
                                    padding: '2px 4px',
                                    fontSize: 9
                                }}>
                                    {analysisResults[photo.uid].condition.condition}
                                    <br />
                                    {analysisResults[photo.uid].condition.confidence}%
                                </div>
                            )}
                            
                            <div style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                display: 'flex',
                                gap: 4
                            }}>
                                <Button 
                                    size="small" 
                                    type="text"
                                    icon={<EyeOutlined />}
                                    onClick={() => handlePreview(photo)}
                                    style={{ 
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        padding: '2px 4px',
                                        height: 'auto'
                                    }}
                                />
                                <Button 
                                    size="small" 
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleRemove(photo)}
                                    style={{ 
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        padding: '2px 4px',
                                        height: 'auto'
                                    }}
                                />
                            </div>
                        </div>
                    ))}

                    {photos.length < maxPhotos && (
                        <div 
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                width: 100,
                                height: 100,
                                border: '2px dashed #d9d9d9',
                                borderRadius: 4,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                background: '#fafafa',
                                transition: 'all 0.3s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#1890ff';
                                e.currentTarget.style.background = '#e6f7ff';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#d9d9d9';
                                e.currentTarget.style.background = '#fafafa';
                            }}
                        >
                            <CameraOutlined style={{ fontSize: 24, color: '#8c8c8c' }} />
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                                Add Photo
                            </div>
                        </div>
                    )}
                </div>
            </Spin>

            <input 
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                        handlePhotoUpload(file);
                    }
                    e.target.value = '';
                }}
            />

            {assetCatId === '8' && (
                <Alert
                    message="Smart Feature Active"
                    description="Photos will be automatically analyzed for registration plates and asset condition"
                    type="info"
                    showIcon
                    closable
                    style={{ marginTop: 8, fontSize: 11 }}
                />
            )}

            <Modal
                visible={previewVisible}
                title="Photo Preview"
                footer={null}
                onCancel={() => setPreviewVisible(false)}
                width={800}
            >
                <img 
                    src={previewImage} 
                    alt="Preview"
                    style={{ width: '100%' }}
                />
            </Modal>
        </div>
    );
};

export default SmartPhotoCapture;